import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import { Button, Input } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Mnemonic } from 'common/src/notecards/types'
import {
    ContentCreatorNotification,
    ContentCreatorMnemonicSuggestionNotification,
} from 'common/src/api/contentFrontendVsContentBackend/types'
import { JEPNotificationType } from 'common/src/api/websiteFrontendVsWebsiteBackend/notifications/types'
import {
    notificationsAtom,
    pendingMnemonicSuggestionAtom,
} from '@/atoms/notifications'
import { toggleNotificationComplete } from '@/api/cb/notification'
import { generateNotecardMnemonic } from '@/api/cb/search'
import { syncErrorMessage, syncSuccessMessage } from '@/utils/syncMessages'
import { displayStringForCommentPostTime } from '@/utils/dateTime'
import styles from './MnemonicSuggestionReviewPanel.module.less'

interface MnemonicSuggestionReviewPanelProps {
    notecardID: string
    currentMnemonic: Mnemonic
    setMnemonic: (newMnemonic: Mnemonic) => void
    isDisabled: boolean
}

// CB serves an id on every notification; the shared type does not carry it.
type Suggestion = ContentCreatorMnemonicSuggestionNotification & {
    id?: string
}

const isMnemonicSuggestionForNotecard = (
    notification: ContentCreatorNotification,
    notecardID: string
): notification is Suggestion =>
    notification.type === JEPNotificationType.MNEMONIC_SUGGESTION &&
    notification.metadata?.notecardID === notecardID

// Side-by-side review of students' proposed mnemonics for the notecard being
// edited. Reads the course's notifications directly rather than only the one
// clicked through, so the panel shows up however you reached the card and can
// surface every outstanding suggestion for it -- dismissed ones stay reachable
// behind a toggle, since dismissing is a triage call, not a delete.
export const MnemonicSuggestionReviewPanel: React.FC<
    MnemonicSuggestionReviewPanelProps
> = (props): ReactElement | null => {
    const [notifications, setNotifications] = useRecoilState(notificationsAtom)
    // Set when the creator clicked a specific suggestion to get here; used only
    // to open on that one rather than the first in the list.
    const clickedThrough = useRecoilValue(pendingMnemonicSuggestionAtom)

    const [activeIndex, setActiveIndex] = useState<number>(0)
    const [isModifying, setIsModifying] = useState<boolean>(false)
    const [modifiedText, setModifiedText] = useState<string>('')
    const [isResolving, setIsResolving] = useState<boolean>(false)
    const [showDismissed, setShowDismissed] = useState<boolean>(false)

    const { open, dismissed } = useMemo((): {
        open: Suggestion[]
        dismissed: Suggestion[]
    } => {
        const forThisCard = (notifications ?? []).filter(
            (notification): notification is Suggestion =>
                isMnemonicSuggestionForNotecard(notification, props.notecardID)
        )
        return {
            open: forThisCard.filter((s) => !s.isCompleted),
            dismissed: forThisCard.filter((s) => s.isCompleted),
        }
    }, [notifications, props.notecardID])

    // Open on the clicked-through suggestion, but SEED activeIndex rather than
    // overriding it -- otherwise the pager below could never move off it.
    const [seededForId, setSeededForId] = useState<string | null>(null)
    useEffect(() => {
        const clickedId = clickedThrough?.id
        if (!clickedId || clickedId === seededForId) return
        const index = open.findIndex((s) => s.id === clickedId)
        if (index < 0) return
        setActiveIndex(index)
        setSeededForId(clickedId)
    }, [clickedThrough, open, seededForId])

    // Resolving shrinks `open`, so always clamp before indexing.
    const effectiveIndex = Math.min(
        Math.max(activeIndex, 0),
        Math.max(open.length - 1, 0)
    )
    const active: Suggestion | undefined = open[effectiveIndex]

    const markCompleted = useCallback(
        async (
            suggestion: Suggestion,
            isCompleted: boolean
        ): Promise<boolean> => {
            if (!suggestion.id) {
                syncErrorMessage(
                    'This suggestion is missing an id — reload and try again'
                )
                return false
            }
            const response = await toggleNotificationComplete(
                suggestion.metadata.courseName,
                suggestion.id,
                isCompleted
            )
            if (!response.data.success) {
                syncErrorMessage('Failed to update the suggestion')
                return false
            }
            // Reflect it locally so the panel updates without a refetch.
            setNotifications(
                (notifications ?? []).map((notification) =>
                    (notification as Suggestion).id === suggestion.id
                        ? { ...notification, isCompleted }
                        : notification
                )
            )
            return true
        },
        [notifications, setNotifications]
    )

    const resolve = useCallback(
        async (
            suggestion: Suggestion,
            newPhrase?: string,
            withAiExplanation?: boolean
        ): Promise<void> => {
            setIsResolving(true)
            try {
                if (newPhrase !== undefined) {
                    // Student suggestions arrive as a bare phrase; optionally
                    // have AI write the matching explanation instead of
                    // carrying the (now possibly stale) existing one forward.
                    let explanation = props.currentMnemonic?.explanation ?? ''
                    if (withAiExplanation) {
                        const response = await generateNotecardMnemonic({
                            courseName: suggestion.metadata.courseName,
                            notecardID: props.notecardID,
                            fixedMnemonic: newPhrase,
                        })
                        if (!response.data.success) {
                            syncErrorMessage(
                                'Failed to write an explanation — the mnemonic was left unchanged'
                            )
                            return
                        }
                        explanation = response.data.payload.explanation
                    }
                    props.setMnemonic({
                        mnemonic: newPhrase,
                        explanation,
                    })
                }
                if (!(await markCompleted(suggestion, true))) return
                setIsModifying(false)
                setModifiedText('')
                // Leave activeIndex alone: the list shrinks under it and the
                // clamp above lands on the next suggestion to look at.
                syncSuccessMessage(
                    newPhrase !== undefined
                        ? withAiExplanation
                            ? 'Mnemonic and AI explanation set — save the notecard to publish them'
                            : 'Mnemonic updated — save the notecard to publish it'
                        : 'Suggestion dismissed'
                )
            } finally {
                setIsResolving(false)
            }
        },
        [props, markCompleted]
    )

    const restore = useCallback(
        async (suggestion: Suggestion): Promise<void> => {
            setIsResolving(true)
            try {
                if (await markCompleted(suggestion, false)) {
                    syncSuccessMessage('Suggestion restored')
                }
            } finally {
                setIsResolving(false)
            }
        },
        [markCompleted]
    )

    const handleStartModifying = useCallback((): void => {
        if (!active) return
        setModifiedText(active.metadata.proposedMnemonic)
        setIsModifying(true)
    }, [active])

    const step = useCallback(
        (delta: number): void => {
            setIsModifying(false)
            setActiveIndex(
                (effectiveIndex + delta + open.length) %
                    Math.max(open.length, 1)
            )
        },
        [effectiveIndex, open.length]
    )

    if (open.length === 0 && dismissed.length === 0) return null

    const currentPhrase = props.currentMnemonic?.mnemonic?.trim()

    return (
        <div className={styles.reviewPanel}>
            <div className={styles.header}>
                <span className={styles.title}>
                    {open.length > 1
                        ? 'Suggested mnemonics (from students)'
                        : 'Suggested mnemonic (from student)'}
                </span>
                {open.length > 1 && (
                    <span className={styles.pager}>
                        <Button
                            type={'text'}
                            size={'small'}
                            icon={<LeftOutlined />}
                            onClick={() => step(-1)}
                            disabled={isResolving}
                            aria-label={'Previous suggestion'}
                        />
                        {effectiveIndex + 1} of {open.length}
                        <Button
                            type={'text'}
                            size={'small'}
                            icon={<RightOutlined />}
                            onClick={() => step(1)}
                            disabled={isResolving}
                            aria-label={'Next suggestion'}
                        />
                    </span>
                )}
            </div>

            {active ? (
                <>
                    <div className={styles.attribution}>
                        {active.metadata.userEmail} ·{' '}
                        {displayStringForCommentPostTime(
                            new Date(active.createdAt)
                        )}
                    </div>

                    <div className={styles.comparison}>
                        <div className={styles.column}>
                            <div className={styles.columnLabel}>Current</div>
                            <div className={styles.phrase}>
                                {currentPhrase || (
                                    <span className={styles.empty}>
                                        No mnemonic yet
                                    </span>
                                )}
                            </div>
                        </div>
                        <div
                            className={`${styles.column} ${styles.proposedColumn}`}
                        >
                            <div className={styles.columnLabel}>Proposed</div>
                            {isModifying ? (
                                <Input.TextArea
                                    className={styles.modifyInput}
                                    value={modifiedText}
                                    onChange={(e) =>
                                        setModifiedText(e.target.value)
                                    }
                                    autoSize={{ minRows: 2, maxRows: 6 }}
                                    autoFocus
                                />
                            ) : (
                                <div className={styles.phrase}>
                                    {active.metadata.proposedMnemonic}
                                </div>
                            )}
                        </div>
                    </div>

                    {props.currentMnemonic?.explanation?.trim() && (
                        <div className={styles.explanationNote}>
                            Plain Accept keeps the existing explanation, which
                            may no longer match the new phrase — “Accept + AI
                            explanation” rewrites it to fit.
                        </div>
                    )}

                    <div className={styles.actions}>
                        {isModifying ? (
                            <>
                                <Button
                                    type={'primary'}
                                    loading={isResolving}
                                    disabled={
                                        props.isDisabled || !modifiedText.trim()
                                    }
                                    onClick={() =>
                                        void resolve(
                                            active,
                                            modifiedText.trim()
                                        )
                                    }
                                >
                                    Save edited mnemonic
                                </Button>
                                <Button
                                    type={'primary'}
                                    ghost
                                    loading={isResolving}
                                    disabled={
                                        props.isDisabled || !modifiedText.trim()
                                    }
                                    onClick={() =>
                                        void resolve(
                                            active,
                                            modifiedText.trim(),
                                            true
                                        )
                                    }
                                >
                                    Save + AI explanation
                                </Button>
                                <Button
                                    onClick={() => setIsModifying(false)}
                                    disabled={isResolving}
                                >
                                    Cancel
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type={'primary'}
                                    loading={isResolving}
                                    disabled={props.isDisabled}
                                    onClick={() =>
                                        void resolve(
                                            active,
                                            active.metadata.proposedMnemonic
                                        )
                                    }
                                >
                                    Accept
                                </Button>
                                <Button
                                    type={'primary'}
                                    ghost
                                    loading={isResolving}
                                    disabled={props.isDisabled}
                                    onClick={() =>
                                        void resolve(
                                            active,
                                            active.metadata.proposedMnemonic,
                                            true
                                        )
                                    }
                                >
                                    Accept + AI explanation
                                </Button>
                                <Button
                                    onClick={handleStartModifying}
                                    disabled={props.isDisabled || isResolving}
                                >
                                    Modify…
                                </Button>
                                <Button
                                    danger
                                    loading={isResolving}
                                    onClick={() => void resolve(active)}
                                >
                                    Dismiss
                                </Button>
                            </>
                        )}
                    </div>
                </>
            ) : (
                <div className={styles.allHandled}>
                    All suggestions for this notecard have been handled.
                </div>
            )}

            {dismissed.length > 0 && (
                <div className={styles.dismissedSection}>
                    <Button
                        type={'text'}
                        size={'small'}
                        className={styles.dismissedToggle}
                        onClick={() => setShowDismissed((v) => !v)}
                    >
                        {showDismissed ? '▾' : '▸'} Dismissed (
                        {dismissed.length})
                    </Button>
                    {showDismissed && (
                        <ul className={styles.dismissedList}>
                            {dismissed.map((suggestion, index) => (
                                <li
                                    key={suggestion.id ?? index}
                                    className={styles.dismissedItem}
                                >
                                    <div className={styles.dismissedText}>
                                        {suggestion.metadata.proposedMnemonic}
                                    </div>
                                    <div className={styles.dismissedMeta}>
                                        {suggestion.metadata.userEmail} ·{' '}
                                        {displayStringForCommentPostTime(
                                            new Date(suggestion.createdAt)
                                        )}
                                    </div>
                                    <Button
                                        size={'small'}
                                        disabled={isResolving}
                                        onClick={() => void restore(suggestion)}
                                    >
                                        Restore
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    )
}
